import { AfterViewInit, Directive } from "@angular/core";

import { StepSummaryModal } from "../step-summary.modal";

import { Conversion, Fiat, Price, Wallet } from "@models";

import { FiatService } from "@main-services/currency/fiat.service";
import { Currency } from "@models/interfaces/currency";
import { stripFunnyInput } from "@helpers/utils";
import { convertFiatToTradable, convertTradableToFiat } from "@helpers/tradable-convert";
import { AccountService } from "../../../../account/services/account.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";

@Directive()
export abstract class SendingComponent<C extends Currency, W extends Wallet>
	extends StepSummaryModal
	implements AfterViewInit
{
	protected category: string = "sending";

	protected price: Price = null;
	protected userWants = "fiat";

	public abstract get wallet(): W;
	public abstract get wallets(): W[];
	public abstract get items(): C[];

	public abstract amountItemTotal(): string;

	protected isMax = false;

	protected constructor(
		protected masterdataService: MasterdataService,
		protected accountService: AccountService,
		protected fiatService: FiatService,
		protected confirmModalService: ConfirmModalService
	) {
		super(confirmModalService);
	}

	public abstract get item(): C;
	public abstract get amount(): string;
	public abstract set amount(s: string);

	public hasWallets(): boolean {
		return this.wallets.length > 0;
	}

	public get icon(): string {
		return this.userWants === "cryptocoin"
			? this.item.getIconCssName(true)
			: `bpi__asset-${this.fiat.getSymbol().toLowerCase()}`;
	}

	public get precision(): number {
		if (this.userWants === "cryptocoin") {
			return this.item.getPrecision();
		}
		return this.fiat.getPrecision();
	}

	public getInputFormat(): string {
		return this.userWants;
	}

	public getAmounts(): Conversion[] {
		if (this.price == null) {
			return [];
		}
		return this.price.getAmounts().slice(0, 2);
	}

	public maxAmount(): Conversion {
		if (this.price == null) {
			return new Conversion();
		}
		return this.price.getMaxAmount();
	}

	public setEdit(n: number, b: boolean): void {
		this.tradableId = this.wallet ? this.wallet.getCurrencyId() : null;
		super.setEdit(n, b);
	}

	public get fiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public getStepsFromOfferFiat(): number {
		return this.fiatService.stepSizeByItem(this.fiat);
	}

	public change(type: string = "fiat"): void {
		if (type === "fiat" || type === "cryptocoin") {
			this.userWants = type;
		} else if (type === "swap") {
			if (this.userWants === "fiat") {
				this.change("cryptocoin");
			} else {
				this.change("fiat");
			}
		}

		this.isMax = false;
	}

	public setAmount(n: Conversion): void {
		if (+n.tradableAmount() === +this.maxAmount().tradableAmount()) {
			this.setMaxAmount();
		} else {
			this.userWants = "fiat";
			this.amount = n.fiatAmount();
			this.track("amount");
		}
	}

	public setMaxAmount(): void {
		this.userWants = "cryptocoin";
		this.amount = this.maxAmount().tradableAmount();
		this.track("amount");
	}

	public toItemAmount(fiatAmount: number): number {
		return convertFiatToTradable(this.fiat, fiatAmount, this.item);
	}

	public toFiatAmount(coinAmount: number): number {
		return convertTradableToFiat(this.item, coinAmount, this.fiat);
	}

	public get sufficientFunds(): boolean {
		let num = this.amountItemTotal();

		num = stripFunnyInput(num);

		return +num <= this.wallet.getBalance() && +num > 0;
	}

	public get multiplyTradableAmountByFiatPrice(): number {
		if (this.userWants === "fiat") {
			return +this.amount;
		}
		if (!this.price || !this.price.getPrice()) {
			return 0;
		}
		const fiatPrice = this.price.getPrice().getFiat();
		return fiatPrice ? +this.amountItemTotal() * +fiatPrice : 0;
	}
}
